/**
 * Which key presses each action-bar slot: an account setting, one key per
 * slot. A slot can take any single printable key (a letter, digit or
 * punctuation key) that no other binding uses; keys other bindings own (I for
 * Inventory, M for the map, O for the commission, …) are locked out, so a
 * slot can never steal one. Assigning a key another slot holds swaps the two.
 */

import { toActionBarBindingId, DEFAULT_ACTION_BAR_KEYS, DEFAULT_INPUT_BINDINGS, normalizeKey } from './input-bindings';
import { ACTION_BAR_HOTKEY_COUNT } from './types/action-bar';
import type { DigitalInputBindingId, InputBindings } from './types/input-bindings';

/** A new account's keys: 1-8, and D on the last slot (Draw). */
export function createDefaultActionBarKeys(): string[] {
    return [...DEFAULT_ACTION_BAR_KEYS];
}

/** Every key a non-action-bar binding uses, with the binding that owns it. */
const RESERVED_ACTION_BAR_KEYS: ReadonlyMap<string, DigitalInputBindingId> = new Map(
    (Object.entries(DEFAULT_INPUT_BINDINGS) as Array<[keyof InputBindings, InputBindings[keyof InputBindings]]>)
        .filter(([id]) => !id.startsWith('action-bar-'))
        .flatMap(([id, bindings]) => bindings.flatMap((binding) => (
            binding.kind === 'digital' && binding.input.device === 'keyboard' && (binding.modifiers ?? []).length === 0
                ? [[normalizeKey(binding.input.key), id as DigitalInputBindingId] as const]
                : []
        ))),
);

/** The binding that owns `key`, so an action-bar slot can't take it; undefined when the key is free. */
export function findActionBarKeyReservation(key: string): DigitalInputBindingId | undefined {
    return RESERVED_ACTION_BAR_KEYS.get(normalizeKey(key));
}

/** Whether an action-bar slot may be put on `key`: a single printable, non-space key no other binding owns. */
export function isAssignableActionBarKey(key: string): boolean {
    return /^[\x21-\x7e]$/.test(key) && findActionBarKeyReservation(key) === undefined;
}

/**
 * `keys` with slot `index` on `key` (normalized). If another slot already had
 * that key, it takes `index`'s old key instead. Returns `keys` unchanged when
 * the index or key isn't valid.
 */
export function withActionBarKeyAssigned(keys: readonly string[], index: number, key: string): string[] {
    if (!Number.isInteger(index) || index < 0 || index >= keys.length || !isAssignableActionBarKey(key)) {
        return [...keys];
    }
    const normalized = normalizeKey(key);
    const next = [...keys];
    const holder = next.indexOf(normalized);
    if (holder !== -1 && holder !== index) {
        next[holder] = next[index]!;
    }
    next[index] = normalized;
    return next;
}

/**
 * Validates an action-bar key list (e.g. from a PATCH body or a stored
 * setting): exactly `ACTION_BAR_HOTKEY_COUNT` distinct assignable keys.
 * Returns them normalized, or undefined when the list isn't valid.
 */
export function parseActionBarKeys(value: unknown): string[] | undefined {
    if (!Array.isArray(value) || value.length !== ACTION_BAR_HOTKEY_COUNT) {
        return undefined;
    }
    const keys: string[] = [];
    for (const entry of value) {
        if (typeof entry !== 'string' || !isAssignableActionBarKey(entry)) {
            return undefined;
        }
        const normalized = normalizeKey(entry);
        if (keys.includes(normalized)) {
            return undefined;
        }
        keys.push(normalized);
    }
    return keys;
}

/** `bindings` with each action-bar slot bound to its key in `keys`, and nothing else changed. */
export function withActionBarKeys(bindings: InputBindings, keys: readonly string[]): InputBindings {
    const next: Record<string, InputBindings[keyof InputBindings]> = { ...bindings };
    keys.forEach((key, index) => {
        const id = toActionBarBindingId(index);
        if (id !== undefined) {
            next[id] = [{ kind: 'digital', input: { device: 'keyboard', key } }];
        }
    });
    return next as unknown as InputBindings;
}
